// Hooks the Plan pages share: the phone breakpoint, and the corkboard's heatmap and cold scenes (the Mac's
// `nodeHeat(in:)` and `isCold(_:hasChildren:)`), read from the per-browser choices the Heatmap menu sets.
import { useEffect, useMemo, useState } from 'react';
import type { OpenedProject, Project, TreeNode } from '@writing-suite/core';
import { DEFAULT_COLD_DAYS, revisionHeat } from '@writing-suite/core/revise';
import { planShowsScenes } from '@writing-suite/core/plan';
import { LAYOUTS, openedAt, PREF, useSharedPreference, type CorkboardLayout } from '../../store/plan.ts';
import { isJournalPosts, plannedPost } from './posts.tsx';
import { heatOfNodes, isColdScene } from '../../store/planModel.ts';

/** Whether a media query matches, following it. */
export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof matchMedia !== 'undefined' && matchMedia(query).matches);
  useEffect(() => {
    const m = matchMedia(query);
    const on = () => setMatches(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return matches;
}

/** A phone: the Mac's iPhone pages (PhonePlanPage, BeatList). */
export const usePhone = () => useMedia('(max-width: 700px)');

export interface PlanHeat {
  /** Heat 1–4 by node (empty with the heatmap off). */
  heat: Record<string, number>;
  cold(node: TreeNode): boolean;
  coldDays: number;
}

/** The heatmap and cold marks — only for the story's scenes (`scenes`: `planShowsScenes`); none for research, notes or
 *  book matter. */
export function usePlanHeat(project: Project, tree: TreeNode[], scenes = true): PlanHeat {
  const [on] = useSharedPreference(PREF.heat, false);
  const [days] = useSharedPreference(PREF.heatDays, 30);
  const [coldDays] = useSharedPreference(PREF.coldDays, DEFAULT_COLD_DAYS);
  const opened = useMemo(() => openedAt(project.id), [project.id]);
  const heat = useMemo(() => {
    const p = project as OpenedProject;
    if (!on || !scenes || typeof p.fragmentRecord !== 'function') return {};
    return heatOfNodes(tree, revisionHeat(p, days));
  }, [on, days, project, tree, scenes]);
  return {
    heat,
    coldDays,
    cold: (n) => scenes && isColdScene(n, n.fragmentId ? project.fragment(n.fragmentId)?.modifiedAt : null, opened, coldDays),
  };
}

/** The corkboard's layout and the layouts offered: a journal's posts have Board, Grid and Freeform (their own choice in
 *  this browser, the Board by default) and inside a post Grid and Freeform; swimlanes only for the story's scenes. */
export function useCorkboardLayout(project: Project, arrangement: { role: string }, tree: TreeNode[], nodeId: string | null) {
  const [book, setBook] = useSharedPreference<CorkboardLayout>(PREF.layout, 'grid');
  const [journal, setJournal] = useSharedPreference<CorkboardLayout>(PREF.journalLayout, 'board');
  const posts = isJournalPosts(project, arrangement);
  const planned = posts ? plannedPost(tree, nodeId) : null;
  const scenes = planShowsScenes(arrangement.role as Parameters<typeof planShowsScenes>[0]);
  const offered = LAYOUTS.filter((l) => (l.id === 'board' ? posts && !planned : l.id !== 'swimlanes' || (scenes && !posts)));
  const chosen = posts ? journal : book;
  const shown: CorkboardLayout = offered.some((l) => l.id === chosen) ? chosen : 'grid';
  return { shown, offered, set: posts ? setJournal : setBook, posts, planned };
}
